import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer, 
    TableHead, TableRow, IconButton, TextField, Typography,
    Button, useTheme, useMediaQuery, Tooltip, Dialog,
    DialogTitle, DialogContent, DialogActions,
    Card, CardContent, Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import { adminApi } from '../../services/api';

const PromptsManager = () => {
    const [prompts, setPrompts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editValues, setEditValues] = useState({});
    const [newPromptDialog, setNewPromptDialog] = useState(false);
    const [newPrompt, setNewPrompt] = useState({
        prompt_name: '',
        prompt_text: ''
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchPrompts();
    }, []);

    const fetchPrompts = async () => {
        try {
            const response = await adminApi.getPrompts();
            setPrompts(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching prompts:', error);
            setPrompts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (prompt) => {
        setEditingId(prompt.id);
        setEditValues({
            prompt_name: prompt.prompt_name || '',
            prompt_text: prompt.prompt_text || ''
        });
    };

    const handleSave = async (id) => {
        try {
            await adminApi.updatePrompt(id, editValues);
            await fetchPrompts();
            setEditingId(null);
        } catch (error) {
            console.error('Error updating prompt:', error);
        }
    };

    const handleCreateDialogOpen = () => {
        setNewPrompt({
            prompt_name: '',
            prompt_text: ''
        });
        setNewPromptDialog(true);
    };

    const handleCreateDialogClose = () => {
        setNewPromptDialog(false);
    };

    const handleCreate = async () => {
        try {
            if (!newPrompt.prompt_name || !newPrompt.prompt_text) {
                alert('Please fill in both name and text fields');
                return;
            }

            await adminApi.createPrompt(newPrompt);
            await fetchPrompts();
            setNewPromptDialog(false);
            setNewPrompt({ prompt_name: '', prompt_text: '' });
        } catch (error) {
            console.error('Error creating prompt:', error);
            alert('Failed to create prompt');
        }
    };

    const renderMobileView = () => (
        <Stack spacing={1}>
            {Array.isArray(prompts) && prompts.map((prompt) => (
                <Card key={prompt.id} variant="outlined">
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Box sx={{ flex: 1, mr: 1 }}>
                                {editingId === prompt.id ? (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={editValues.prompt_name}
                                        onChange={(e) => setEditValues({
                                            ...editValues,
                                            prompt_name: e.target.value
                                        })}
                                        sx={{ mb: 1 }}
                                    />
                                ) : (
                                    <Typography 
                                        variant="subtitle2" 
                                        sx={{ fontWeight: 500, mb: 0.5 }}
                                    >
                                        {prompt.prompt_name}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {editingId === prompt.id ? (
                                    <>
                                        <IconButton 
                                            onClick={() => handleSave(prompt.id)}
                                            color="primary"
                                            size="small"
                                        >
                                            <SaveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => setEditingId(null)}
                                            size="small"
                                        >
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <IconButton 
                                        onClick={() => handleEdit(prompt)}
                                        size="small"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>

                        {editingId === prompt.id ? (
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                size="small"
                                value={editValues.prompt_text}
                                onChange={(e) => setEditValues({
                                    ...editValues,
                                    prompt_text: e.target.value
                                })}
                            />
                        ) : (
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    fontSize: '0.875rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {prompt.prompt_text}
                            </Typography>
                        )}

                        <Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}
                        >
                            Last updated: {prompt.updated ? new Date(prompt.updated).toLocaleString() : 'Never'}
                        </Typography>
                    </CardContent>
                </Card>
            ))}
        </Stack>
    );

    const renderDesktopView = () => (
        <TableContainer component={Paper}>
            <Table 
                size="small" 
                sx={{
                    '& .MuiTableCell-root': {
                        py: 1.5,
                        '&.text-cell': {
                            maxWidth: '50vw', // Limit text cell width
                            width: '50%' // Give text column consistent width
                        }
                    }
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell className="text-cell">Text</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell align="right" sx={{ width: '120px' }}>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.isArray(prompts) && prompts.map((prompt) => (
                        <TableRow key={prompt.id}>
                            <TableCell>
                                {editingId === prompt.id ? (
                                    <TextField
                                        size="small"
                                        fullWidth
                                        value={editValues.prompt_name}
                                        onChange={(e) => setEditValues({
                                            ...editValues,
                                            prompt_name: e.target.value
                                        })}
                                    />
                                ) : (
                                    <Tooltip title={prompt.prompt_name}>
                                        <Typography noWrap>
                                            {prompt.prompt_name}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </TableCell>
                            <TableCell className="text-cell">
                                {editingId === prompt.id ? (
                                    <TextField
                                        multiline
                                        rows={3}
                                        fullWidth
                                        size="small"
                                        value={editValues.prompt_text}
                                        onChange={(e) => setEditValues({
                                            ...editValues,
                                            prompt_text: e.target.value
                                        })}
                                    />
                                ) : (
                                    <Tooltip title={prompt.prompt_text}>
                                        <Typography 
                                            sx={{ 
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                lineHeight: 1.4
                                            }}
                                        >
                                            {prompt.prompt_text}
                                        </Typography>
                                    </Tooltip>
                                )}
                            </TableCell>
                            <TableCell>
                                {prompt.updated ? new Date(prompt.updated).toLocaleString() : 'Never'}
                            </TableCell>
                            <TableCell align="right">
                                {editingId === prompt.id ? (
                                    <>
                                        <IconButton 
                                            onClick={() => handleSave(prompt.id)}
                                            color="primary"
                                            size="small"
                                        >
                                            <SaveIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            onClick={() => setEditingId(null)}
                                            size="small"
                                        >
                                            <CancelIcon fontSize="small" />
                                        </IconButton>
                                    </>
                                ) : (
                                    <IconButton 
                                        onClick={() => handleEdit(prompt)}
                                        size="small"
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Box sx={{ height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ 
                mb: isMobile ? 1 : 2, 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant={isMobile ? "subtitle1" : "h5"}>
                    Recruitment Prompts
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={handleCreateDialogOpen}
                    size={isMobile ? "small" : "medium"}
                >
                    Add New
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {isMobile ? renderMobileView() : renderDesktopView()}
            </Box>

            {/* New Prompt Dialog */}
            <Dialog 
                open={newPromptDialog} 
                onClose={handleCreateDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Create New Prompt</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Prompt Name"
                            fullWidth
                            value={newPrompt.prompt_name}
                            onChange={(e) => setNewPrompt({
                                ...newPrompt,
                                prompt_name: e.target.value
                            })}
                            placeholder="Enter prompt name"
                        />
                        <TextField
                            label="Prompt Text"
                            fullWidth
                            multiline
                            rows={4}
                            value={newPrompt.prompt_text}
                            onChange={(e) => setNewPrompt({
                                ...newPrompt,
                                prompt_text: e.target.value
                            })}
                            placeholder="Enter prompt text"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateDialogClose}>Cancel</Button>
                    <Button 
                        onClick={handleCreate} 
                        variant="contained" 
                        color="primary"
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PromptsManager; 